import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Button,
  Form,
  Modal,
  Dropdown,
  Grid,
  Input,
  Message,
} from 'semantic-ui-react';
import { GISLayer, LayerProperty } from './gisReducer';
import { postWithAuth } from 'src/api';
import messages from 'src/messages';
import { useIntl } from 'react-intl';

interface LayerConfigModalProps {
  layer: GISLayer;
  projectId: string;
  onConfigChange: (config: LayerProperty) => void;
}

const LayerConfigModal: React.FC<LayerConfigModalProps> = ({
  layer,
  onConfigChange,
  projectId,
}) => {
  const { formatMessage } = useIntl();
  const [open, setOpen] = useState(false);
  const { kind, config, id: layerId } = layer;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Default values based on layer type
  const getDefaultValues = (): LayerProperty => ({
    color: '#ff0000',
    opacity: 1,
    weight: 3,
    radius: 8,
    fillColor: '#3388ff',
    fillOpacity: 0.2,
    dashArray: 'solid',
    ...config,
  });

  const { control, handleSubmit, reset } = useForm<LayerProperty>({
    defaultValues: getDefaultValues(),
  });

  const openModal = () => {
    reset(getDefaultValues());
    setOpen(true);
  };

  const saveConfig = async (data: LayerProperty) => {
    setLoading(true);
    setError(null);

    try {
      await postWithAuth(
        `project/${projectId}/gis_layers/${layerId}`,
        data,
        'PATCH'
      );

      if (onConfigChange) {
        onConfigChange(data);
      }
      setOpen(false);
    } catch (err) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return <Button icon="cog" onClick={openModal} />;
  }

  return (
    <>
      <Button icon="cog" onClick={openModal} />

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        size="tiny"
        style={{ width: kind === 'Overlay' ? '250px' : '400px' }}
      >
        <Modal.Header>
          {kind.replace('LineString', 'Line')} Layer Style
          <Button
            circular
            icon="close"
            title="Close"
            floated="right"
            onClick={() => setOpen(false)}
          />
        </Modal.Header>
        <Modal.Content>
          {error && (
            <Message negative>
              <Message.Header>{formatMessage(messages.error)}</Message.Header>
              <p>{error}</p>
            </Message>
          )}
          <Form
            id="layer-form"
            onSubmit={handleSubmit(saveConfig)}
            loading={loading}
          >
            {kind === 'LineString' && <LineStringConfig control={control} />}
            {kind === 'Point' && <PointConfig control={control} />}
            {kind === 'Polygon' && <PolygonConfig control={control} />}
            {kind === 'Overlay' && <OverlayConfig control={control} />}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button
            type="button"
            onClick={() => setOpen(false)}
            disabled={loading}
          >
            {formatMessage(messages.cancel)}
          </Button>
          <Button color="blue" form="layer-form" type="submit">
            {formatMessage(messages.ok)}
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

function PolygonConfig({ control }) {
  return (
    <Grid columns={2}>
      <Grid.Column>
        <Form.Field>
          <label>Color</label>
          <Controller
            name="color"
            control={control}
            render={({ field }) => (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  {...field}
                  type="color"
                  style={{ width: '50px', marginRight: '10px' }}
                />
                <Input
                  className="gis-layer-color"
                  value={field.value}
                  onChange={(e, { value }) => field.onChange(value)}
                  maxLength={7}
                />
              </div>
            )}
          />
        </Form.Field>
      </Grid.Column>
      <Grid.Column>
        <Form.Field>
          <label>Opacity</label>
          <Controller
            name="opacity"
            control={control}
            render={({ field }) => (
              <Input
                type="range"
                step={0.1}
                min={0.1}
                max={1}
                value={field.value}
                onChange={(e, { value }) => field.onChange(Number(value))}
              />
            )}
          />
        </Form.Field>
      </Grid.Column>
    </Grid>
  );
}

function PointConfig({ control }) {
  const markerShapeOptions = [
    { key: 'circle', value: 'circle', text: 'Circle', icon: 'circle' },
    { key: 'square', value: 'square', text: 'Square', icon: 'square' },
    {
      key: 'triangle',
      value: 'triangle',
      text: 'Triangle',
      icon: 'triangle up',
    },
    { key: 'pin', value: 'pin', text: 'Pin', icon: 'map pin' },
  ];
  return (
    <Grid columns={2}>
      <Grid.Column>
        <Form.Field>
          <label>Color</label>
          <Controller
            name="color"
            control={control}
            render={({ field }) => (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  {...field}
                  type="color"
                  style={{ width: '50px', marginRight: '10px' }}
                />
                <Input
                  className="gis-layer-color"
                  value={field.value}
                  onChange={(e, { value }) => field.onChange(value)}
                  maxLength={7}
                />
              </div>
            )}
          />
        </Form.Field>

        <Form.Field>
          <label>Opacity</label>
          <Controller
            name="opacity"
            control={control}
            render={({ field }) => (
              <Input
                type="range"
                step={0.1}
                min={0}
                max={1}
                value={field.value}
                onChange={(e, { value }) => field.onChange(Number(value))}
              />
            )}
          />
        </Form.Field>
      </Grid.Column>

      <Grid.Column>
        <Form.Field>
          <label>Marker Size</label>
          <Controller
            name="radius"
            control={control}
            render={({ field }) => (
              <Input
                type="range"
                min={4}
                max={12}
                step={1}
                value={field.value}
                onChange={(e, { value }) => field.onChange(Number(value))}
              />
            )}
          />
        </Form.Field>
        <Form.Field>
          <label>Marker Shape</label>
          <Controller
            name="markerShape"
            control={control}
            render={({ field }) => (
              <Dropdown
                selection
                options={markerShapeOptions}
                value={field.value || 'pin'}
                onChange={(_, { value }) => field.onChange(value)}
                style={{ width: '100%' }}
              />
            )}
          />
        </Form.Field>
      </Grid.Column>
    </Grid>
  );
}

function LineStringConfig({ control }) {
  const lineStyleOptions = [
    {
      key: 'solid',
      value: 'solid',
      text: 'Solid',
      content: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span
            style={{
              marginRight: '10px',
              width: '40px',
              height: '2px',
              backgroundColor: '#000',
            }}
          />
          Solid
        </div>
      ),
    },
    {
      key: 'dash',
      value: 'dashed',
      text: 'Dashed',
      content: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <svg width="40" height="2">
            <line
              x1="0"
              y1="1"
              x2="40"
              y2="1"
              stroke="#000"
              strokeWidth="2"
              strokeDasharray="5,5"
            />
          </svg>
          Dashed
        </div>
      ),
    },
    {
      key: 'dot',
      value: 'dotted',
      text: 'Dotted',
      content: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <svg width="40" height="2">
            <line
              x1="0"
              y1="1"
              x2="40"
              y2="1"
              stroke="#000"
              strokeWidth="2"
              strokeDasharray="1,5"
            />
          </svg>
          Dotted
        </div>
      ),
    },
  ];

  return (
    <Grid columns={2}>
      <Grid.Column>
        <Form.Field>
          <label>Color</label>
          <Controller
            name="color"
            control={control}
            render={({ field }) => (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  {...field}
                  type="color"
                  style={{ width: '50px', marginRight: '10px' }}
                />
                <Input
                  className="gis-layer-color"
                  value={field.value}
                  onChange={(e, { value }) => field.onChange(value)}
                  maxLength={7}
                />
              </div>
            )}
          />
        </Form.Field>

        <Form.Field>
          <label>Opacity</label>
          <Controller
            name="opacity"
            control={control}
            render={({ field }) => (
              <Input
                type="range"
                step={0.1}
                min={0}
                max={1}
                value={field.value}
                onChange={(e, { value }) => field.onChange(Number(value))}
              />
            )}
          />
        </Form.Field>
      </Grid.Column>

      <Grid.Column>
        <Form.Field>
          <label>Line Thickness</label>
          <Controller
            name="weight"
            control={control}
            render={({ field }) => (
              <Input
                type="range"
                min={1}
                max={15}
                step={2}
                value={field.value}
                onChange={(e, { value }) => field.onChange(Number(value))}
              />
            )}
          />
        </Form.Field>

        <Form.Field>
          <label>Line Style</label>
          <Controller
            name="dashArray"
            control={control}
            render={({ field }) => (
              <Dropdown
                selection
                options={lineStyleOptions}
                value={field.value || 'solid'}
                onChange={(_, data) => field.onChange(data.value)}
              />
            )}
          />
        </Form.Field>
      </Grid.Column>
    </Grid>
  );
}

function OverlayConfig({ control }) {
  return (
    <Grid columns={1}>
      <Grid.Column>
        <Form.Field>
          <label>Opacity</label>
          <Controller
            name="opacity"
            control={control}
            render={({ field }) => (
              <Input
                type="range"
                step={0.1}
                min={0.1}
                max={1}
                value={field.value}
                onChange={(e, { value }) => field.onChange(Number(value))}
              />
            )}
          />
        </Form.Field>
      </Grid.Column>
    </Grid>
  );
}

export default LayerConfigModal;
